import { useSurveyStore } from "../store/useSurveyStore";
import apiClient from "../api/apiClient";
import { useState, useEffect, useMemo } from "react";
import {
	Box,
	Typography,
	Radio,
	RadioGroup,
	FormControlLabel,
	FormControl,
	Button,
	Paper,
	CircularProgress,
	Backdrop,
	Select,
	MenuItem,
	TextField,
	Checkbox,
} from "@mui/material";
import mixpanel from "@/utils/analytics";
import { isMockMode } from "@/utils/mockSurvey";

export default function Survey({ onFinish }) {
	const {
		questions,
		answers,
		setAnswer,
		token,
		complianceInput,
		complianceTimestamp,
		initTimestamp,
		surveyTitle,
		surveySubTitle,
		surveyIntroTextHtml,
	} = useSurveyStore();
	const [allRequiredAnswered, setAllRequiredAnswered] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Sort questions by position (asc) and id (desc)
	const sortedQuestions = useMemo(() => {
		return [...questions].sort((a, b) => {
			// First sort by position
			if (a.position !== b.position) {
				return a.position - b.position;
			}
			// If positions are equal, sort by id in descending order
			return b.id - a.id;
		});
	}, [questions]);

	// Build question number map, skipping info items
	const questionNumberMap = {};
	let questionCounter = 0;
	sortedQuestions.forEach((q, i) => {
		if (q.question_type !== "info") {
			questionCounter++;
			questionNumberMap[i] = questionCounter;
		}
	});

	// Check if all required questions are answered
	useEffect(() => {
		const requiredUnanswered = sortedQuestions.filter((q) => {
			// Skip validation for info type questions
			if (q.question_type === "info") return false;

			if (q.is_optional) return false;

			const answer = answers[q.id];
			if (!answer) return true;

			// For text-based questions, check if the text is not empty
			if (q.question_type === "singletext" || q.question_type === "fulltext") {
				return answer.trim().length === 0;
			}

			// For checkboxes, check if at least one option is selected
			if (q.question_type === "checkboxes") {
				return answer.length === 0;
			}

			// For numeric questions, check if the value is not empty
			if (q.question_type === "numeric") {
				return answer.trim().length === 0;
			}

			// For other question types (radioboxes, dropdown1), just checking existence is enough
			return false;
		});

		setAllRequiredAnswered(requiredUnanswered.length === 0);
	}, [sortedQuestions, answers]);

	const handleFinish = () => {
		// Set submitting state to show loading animation and disable form
		setIsSubmitting(true);

		// Format answers for submission
		const formattedAnswers = Object.entries(answers)
			.map(([questionId, answerValue]) => {
				// Find the question to determine its type
				const question = questions.find((q) => q.id === parseInt(questionId));

				// For text-based questions, send the text value
				if (question && (question.question_type === "singletext" || question.question_type === "fulltext")) {
					return [
						{
							question_id: parseInt(questionId),
							answer_text: answerValue,
						},
					];
				}

				// For numeric questions, send the value as answer_text
				if (question && question.question_type === "numeric") {
					return [
						{
							question_id: parseInt(questionId),
							answer_text: answerValue,
						},
					];
				}

				// For checkbox questions, we need to create multiple answer entries
				if (question && question.question_type === "checkboxes") {
					// If no answers selected, return an empty array
					if (!answerValue || answerValue.length === 0) {
						return [];
					}

					// Create an entry for each selected answer
					return answerValue.map((id) => ({
						question_id: parseInt(questionId),
						answer_id: parseInt(id),
					}));
				}

				// For other question types, return a single entry with answer_id
				return [
					{
						question_id: parseInt(questionId),
						answer_id: parseInt(answerValue),
					},
				];
			})
			.flat(); // Flatten the array to handle checkbox answers

		const numberOfQuestions = Object.keys(answers).length;

		// Prepare submission data
		const submissionData = {
			token,
			compliance_declaration: complianceInput,
			compliance_timestamp: complianceTimestamp,
			init_timestamp: initTimestamp,
			answers: formattedAnswers,
		};

		const durationMs = Date.now() - new Date(initTimestamp.replace(" ", "T") + "Z");

		// Mock mode: skip the real API call, log the payload, jump to thank-you
		if (isMockMode()) {
			console.log("[mock] survey submission payload:", submissionData);
			console.log("[mock] duration ms:", durationMs, "questions:", numberOfQuestions);
			setTimeout(() => {
				setIsSubmitting(false);
				onFinish();
			}, 600);
			return;
		}

		// Submit the survey
		apiClient
			.post("/research/submit", submissionData)
			.then((response) => {
				// Check if the response has an error status
				if (response.data && response.data.status === "error") {
					mixpanel.track("survey_error", {
						error: response.data.code,
					});
					// The error code will be captured by the interceptor
					onFinish("error");
					return;
				}

				// track it
				mixpanel.track("survey_completed", {
					init_timestamp: initTimestamp,
					compliance_timestamp: complianceTimestamp,
					survey_duration_ms: durationMs,
					number_of_questions: numberOfQuestions,
				});
				// Success - proceed to thank you page
				onFinish();
			})
			.catch((error) => {
				// The error code will be captured by the interceptor
				console.error("Survey submission error:", error);
				mixpanel.track("survey_error", {
					error: error,
					init_timestamp: initTimestamp,
					compliance_timestamp: complianceTimestamp,
					survey_duration_ms: durationMs,
					number_of_questions: numberOfQuestions,
				});
				onFinish("error");
			})
			.finally(() => {
				// Reset submitting state (though we should be navigating away)
				setIsSubmitting(false);
			});
	};

	return (
		<Box
			sx={{
				maxWidth: "100%",
				padding: "16px",
				display: "flex",
				flexDirection: "column",
				gap: "24px",
				position: "relative",
			}}
		>
			{/* Loading backdrop */}
			<Backdrop
				sx={{
					color: "#fff",
					zIndex: (theme) => theme.zIndex.drawer + 1,
					display: "flex",
					flexDirection: "column",
					gap: "16px",
				}}
				open={isSubmitting}
			>
				<CircularProgress color="inherit" size={60} />
				<Typography variant="h6">Submitting your survey...</Typography>
				<Typography variant="body2">Please wait while we process your responses.</Typography>
			</Backdrop>

			{/* Survey Header */}
			<Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
				{surveyTitle && (
					<Typography variant="h5" component="h4">
						{surveyTitle}
					</Typography>
				)}
				{surveySubTitle && (
					<Typography variant="h6" color="text.secondary">
						{surveySubTitle}
					</Typography>
				)}
				{surveyIntroTextHtml && (
					<Box
						sx={{
							"& p": { marginBottom: "0.5rem" },
							"& p:last-child": { marginBottom: 0 },
						}}
						dangerouslySetInnerHTML={{ __html: surveyIntroTextHtml }}
					/>
				)}
			</Box>

			{/* Questions */}
			{sortedQuestions.map((question, index) => {
				// Sort answers by position (asc) and id (desc)
				const sortedAnswers = [...question.answers].sort((a, b) => {
					// First sort by position
					if (a.position !== b.position) {
						return a.position - b.position;
					}
					// If positions are equal, sort by id in descending order
					return b.id - a.id;
				});

				return (
					<Paper
						key={question.id}
						elevation={1}
						sx={{
							padding: "16px",
							borderRadius: "8px",
							border: "1px solid #e0e0e0",
							opacity: isSubmitting ? 0.7 : 1,
							pointerEvents: isSubmitting ? "none" : "auto",
						}}
					>
						<Typography
							variant="h6"
							component="h2"
							gutterBottom
							sx={{
								fontWeight: "medium",
								color: "text.primary",
								whiteSpace: "pre-line",
							}}
						>
							{question.question_type !== "info" && <>{questionNumberMap[index]}. </>}{question.question}
							{question.is_optional === 1 && question.question_type !== "info" && (
								<Typography
									component="span"
									variant="caption"
									sx={{
										marginLeft: "8px",
										color: "text.secondary",
										fontStyle: "italic",
									}}
								>
									{" "}(Optional)
								</Typography>
							)}
						</Typography>

						{question.question_type === "info" ? (
							<Typography variant="body1" sx={{ fontStyle: "italic", color: "text.secondary" }}>
								{question.description || "Information for the following questions"}
							</Typography>
						) : question.question_type === "radioboxes" ? (
							<FormControl component="fieldset" sx={{ width: "100%" }}>
								<RadioGroup
									value={answers[question.id] || ""}
									onChange={(e) => setAnswer(question.id, e.target.value)}
									disabled={isSubmitting}
								>
									{sortedAnswers.map((answer) => (
										<FormControlLabel
											key={answer.id}
											value={answer.id.toString()}
											control={
												<Radio
													sx={{
														padding: "4px",
														"& .MuiSvgIcon-root": {
															fontSize: 24,
														},
													}}
												/>
											}
											label={<Typography variant="body1">{answer.label}</Typography>}
											sx={{
												margin: "3px 0",
												padding: "4px 6px",
												borderRadius: "4px",
												gap: "12px",
												"&:hover": {
													backgroundColor: "rgba(0, 0, 0, 0.04)",
												},
											}}
										/>
									))}
								</RadioGroup>
							</FormControl>
						) : question.question_type === "checkboxes" ? (
							<FormControl component="fieldset" sx={{ width: "100%" }}>
								{sortedAnswers.map((answer) => {
									// Initialize the answer array if it doesn't exist
									const currentAnswers = answers[question.id] || [];

									return (
										<FormControlLabel
											key={answer.id}
											control={
												<Checkbox
													checked={currentAnswers.includes(answer.id.toString())}
													onChange={(e) => {
														let newAnswers;
														if (!e.target.checked) {
															// Unchecking: just remove this option
															newAnswers = currentAnswers.filter((id) => id !== answer.id.toString());
														} else if (answer.is_exclusive === 1) {
															// Checking an exclusive option: clear everything else
															newAnswers = [answer.id.toString()];
														} else {
															// Checking a regular option: add it and remove any exclusive options
															const exclusiveIds = sortedAnswers
																.filter((a) => a.is_exclusive === 1)
																.map((a) => a.id.toString());
															newAnswers = [
																...currentAnswers.filter((id) => !exclusiveIds.includes(id)),
																answer.id.toString(),
															];
														}
														setAnswer(question.id, newAnswers);
													}}
													disabled={isSubmitting}
													sx={{
														padding: "4px",
														"& .MuiSvgIcon-root": {
															fontSize: 24,
														},
													}}
												/>
											}
											label={<Typography variant="body1">{answer.label}</Typography>}
											sx={{
												margin: "3px 0",
												padding: "4px 6px",
												borderRadius: "4px",
												gap: "12px",
												"&:hover": {
													backgroundColor: "rgba(0, 0, 0, 0.04)",
												},
											}}
										/>
									);
								})}
							</FormControl>
						) : question.question_type === "dropdown1" ? (
							<FormControl fullWidth>
								<Select
									value={answers[question.id] || ""}
									onChange={(e) => setAnswer(question.id, e.target.value)}
									disabled={isSubmitting}
									displayEmpty
									sx={{
										"& .MuiSelect-select": {
											padding: "12px",
										},
									}}
								>
									<MenuItem value="" disabled>
										<em>Select an option</em>
									</MenuItem>
									{sortedAnswers.map((answer) => (
										<MenuItem key={answer.id} value={answer.id.toString()}>
											{answer.label}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						) : question.question_type === "singletext" ? (
							<TextField
								fullWidth
								value={answers[question.id] || ""}
								onChange={(e) => setAnswer(question.id, e.target.value)}
								disabled={isSubmitting}
								placeholder="Enter your answer"
								sx={{
									"& .MuiOutlinedInput-root": {
										padding: "4px",
									},
								}}
							/>
						) : question.question_type === "fulltext" ? (
							<TextField
								fullWidth
								multiline
								rows={4}
								value={answers[question.id] || ""}
								onChange={(e) => setAnswer(question.id, e.target.value)}
								disabled={isSubmitting}
								placeholder="Enter your answer"
								sx={{
									"& .MuiOutlinedInput-root": {
										padding: "8px",
									},
								}}
							/>
						) : question.question_type === "numeric" ? (
							<Box>
								<TextField
									fullWidth
									value={answers[question.id] || ""}
									onChange={(e) => {
										// Only allow numeric input (0-9)
										const value = e.target.value.replace(/[^0-9]/g, "");

										// Validate against min and max if both are present
										if (question.min_numeric_value !== undefined && question.max_numeric_value !== undefined) {
											const numValue = parseInt(value) || 0;
											if (numValue < question.min_numeric_value) {
												setAnswer(question.id, question.min_numeric_value.toString());
											} else if (numValue > question.max_numeric_value) {
												setAnswer(question.id, question.max_numeric_value.toString());
											} else {
												setAnswer(question.id, value);
											}
										} else {
											setAnswer(question.id, value);
										}
									}}
									disabled={isSubmitting}
									placeholder="Enter a number"
									inputProps={{
										inputMode: "numeric",
										pattern: "[0-9]*",
									}}
									sx={{
										"& .MuiOutlinedInput-root": {
											padding: "4px",
										},
									}}
								/>
								{question.min_numeric_value !== undefined && question.max_numeric_value !== undefined && (
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{
											display: "block",
											mt: 0.5,
										}}
									>
										Between {question.min_numeric_value} and {question.max_numeric_value}
									</Typography>
								)}
							</Box>
						) : null}
					</Paper>
				);
			})}

			<Button
				variant="contained"
				color="primary"
				size="large"
				fullWidth
				onClick={handleFinish}
				disabled={!allRequiredAnswered || isSubmitting}
				sx={{
					padding: "12px",
					fontSize: "1.1rem",
					borderRadius: "8px",
					marginTop: "16px",
				}}
			>
				{isSubmitting ? "Submitting..." : "Submit Survey"}
			</Button>

			{!allRequiredAnswered && (
				<Typography variant="body2" color="error" align="center" sx={{ marginTop: "8px" }}>
					Please answer all required questions before submitting
				</Typography>
			)}
		</Box>
	);
}
